import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  MdDirectionsCar,
  MdPerson,
  MdPhone,
  MdRefresh,
  MdAdd,
  MdTrendingUp,
  MdTrendingDown,
  MdCalculate,
  MdSend,
  MdSpeed,
} from "react-icons/md";
import "./CabVendorDashboard.css";

const API = `${
  process.env.REACT_APP_API_URL || "https://bmtvendorvendorfinal.onrender.com"
}/api/cabs/vendor/dashboard`;

const RANGES = [
  { id: "today", label: "Today", noun: "Today's" },
  { id: "week", label: "Last 7 days", noun: "7-day" },
  { id: "month", label: "Last 30 days", noun: "30-day" },
];

const RIDE_FILTERS = [
  { id: "all", label: "All" },
  { id: "STARTED", label: "In transit" },
  { id: "UPCOMING", label: "Upcoming" },
  { id: "PENDING", label: "Pending" },
  { id: "COMPLETED", label: "Completed" },
];

const money = (n) => `₹ ${(Number(n) || 0).toLocaleString("en-IN")}`;
const digitsOnly = (v) => String(v || "").replace(/\D/g, "");

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d)
    ? ""
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

const statusView = (status) => {
  switch (status) {
    case "COMPLETED":
      return { cls: "completed", text: "✓ Completed" };
    case "STARTED":
      return { cls: "transit", text: "● In transit" };
    case "PENDING":
      return { cls: "confirmed", text: "Pending" };
    case "CANCELLED":
      return { cls: "confirmed", text: "Cancelled" };
    case "DRIVER_ASSIGNED":
      return { cls: "confirmed", text: "Driver assigned" };
    default:
      return { cls: "confirmed", text: "Confirmed" };
  }
};

const matchesFilter = (ride, filter) => {
  if (filter === "all") return true;
  if (filter === "UPCOMING") return ["CONFIRMED", "DRIVER_ASSIGNED"].includes(ride.status);
  return ride.status === filter;
};

function Trend({ value }) {
  if (value === null || value === undefined) return null;
  const up = value >= 0;
  return (
    <span className="cvd-trend-pill" style={up ? undefined : { color: "#dc2626" }}>
      {up ? <MdTrendingUp /> : <MdTrendingDown />} {up ? "+" : ""}
      {value}%
    </span>
  );
}

export default function CabVendorDashboard({ onNavigate }) {
  const token =
    localStorage.getItem("vendorToken") || localStorage.getItem("token");

  const [range, setRange] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [rideFilter, setRideFilter] = useState("all");
  const [calcDistance, setCalcDistance] = useState(20);
  const [calcType, setCalcType] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(API, {
        params: { range },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data?.data || null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Could not load dashboard data."
      );
    } finally {
      setLoading(false);
    }
  }, [range, token]);

  useEffect(() => {
    load();
  }, [load]);

  const kpis = data?.kpis;
  const fleet = data?.fleet;
  const byType = useMemo(() => data?.byType || [], [data]);
  const drivers = data?.drivers || [];
  const rides = data?.recentBookings || [];
  const rangeInfo = RANGES.find((r) => r.id === range);

  // Default selected cab type = first real type from backend
  useEffect(() => {
    if (byType.length && !byType.some((t) => t.cabType === calcType)) {
      setCalcType(byType[0].cabType);
    }
  }, [byType, calcType]);

  const selectedType = byType.find((t) => t.cabType === calcType);
  const baseFare = selectedType?.avgBaseFare || 0;
  const perKm = selectedType?.avgPerKm || 0;
  const kmCharge = Math.round(calcDistance * perKm);

  const filteredRides = rides.filter((r) => matchesFilter(r, rideFilter));
  const availablePct = fleet?.total
    ? Math.round((fleet.available / fleet.total) * 100)
    : 0;

  if (loading && !data) {
    return (
      <div className="cvd-container">
        <div className="cvd-panel" style={{ padding: 32, textAlign: "center" }}>
          <strong>Loading dashboard…</strong>
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="cvd-container">
        <div className="cvd-panel" style={{ padding: 32, textAlign: "center" }}>
          <strong style={{ color: "#dc2626" }}>{error}</strong>
          <div style={{ marginTop: 14 }}>
            <button type="button" className="cvd-btn-primary" onClick={load}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cvd-container">
      {/* HEADER */}
      <header className="cvd-header">
        <div>
          <div className="cvd-title-row">
            <h1>Cab fleet & dispatch</h1>
          </div>
          <p>Live numbers from your bookings, fleet and drivers.</p>
        </div>

        <div className="cvd-header-right">
          <div className="cvd-timeframe-group">
            {RANGES.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`cvd-timeframe-btn ${range === t.id ? "active" : ""}`}
                onClick={() => setRange(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="cvd-btn-refresh"
            onClick={load}
            disabled={loading}
            title="Refresh"
          >
            <MdRefresh />
          </button>

          <button
            type="button"
            className="cvd-btn-primary"
            onClick={() => onNavigate("cabs_manage")}
          >
            <MdAdd style={{ fontSize: "18px" }} />
            Add New Cab
          </button>
        </div>
      </header>

      {error && (
        <div className="cvd-panel" style={{ padding: 12, marginBottom: 16, color: "#dc2626" }}>
          {error}
        </div>
      )}

      {/* KPI CARDS */}
      <section className="cvd-kpi-grid">
        <div className="cvd-kpi-card" onClick={() => onNavigate("cabs_bookings")}>
          <div className="cvd-kpi-header">
            <span className="cvd-kpi-title">{rangeInfo.noun} revenue</span>
            <Trend value={kpis?.revenueChange} />
          </div>
          <div className="cvd-kpi-main">
            <strong>{money(kpis?.revenue)}</strong>
          </div>
          <div className="cvd-kpi-footer">
            <span>
              {kpis?.avgFare ? `Avg ${money(kpis.avgFare)} per paid booking` : "No paid bookings yet"}
            </span>
            <span className="cvd-kpi-link">View bookings ➔</span>
          </div>
          <div className="cvd-kpi-glow glow-green" />
        </div>

        <div className="cvd-kpi-card" onClick={() => onNavigate("cabs_bookings")}>
          <div className="cvd-kpi-header">
            <span className="cvd-kpi-title">{rangeInfo.noun} bookings</span>
            <Trend value={kpis?.ridesChange} />
          </div>
          <div className="cvd-kpi-main">
            <strong style={{ color: "#2563eb" }}>{kpis?.rides || 0}</strong>
            <small style={{ fontSize: "14px", color: "#64748b", marginLeft: "6px" }}>
              bookings
            </small>
          </div>
          <div className="cvd-kpi-footer">
            <span>
              {kpis?.completed || 0} completed · {kpis?.cancelled || 0} cancelled
            </span>
            <span className="cvd-kpi-link">Track ➔</span>
          </div>
          <div className="cvd-kpi-glow glow-blue" />
        </div>

        <div className="cvd-kpi-card" onClick={() => onNavigate("cabs_manage")}>
          <div className="cvd-kpi-header">
            <span className="cvd-kpi-title">Fleet available</span>
            <span className="cvd-stat-badge-green">
              {fleet?.available || 0} / {fleet?.total || 0} ready
            </span>
          </div>
          <div className="cvd-kpi-main">
            <strong style={{ color: "#10b981" }}>{fleet?.available || 0} cabs</strong>
          </div>
          <div className="cvd-kpi-progress-wrap">
            <div className="cvd-kpi-progress-bar" style={{ width: `${availablePct}%` }} />
          </div>
          <div className="cvd-kpi-footer">
            <span>
              {fleet?.onTrip || 0} on trip · {fleet?.inactive || 0} inactive
            </span>
            <span className="cvd-kpi-link">Manage fleet ➔</span>
          </div>
          <div className="cvd-kpi-glow glow-teal" />
        </div>

        <div className="cvd-kpi-card" onClick={() => onNavigate("cabs_bookings")}>
          <div className="cvd-kpi-header">
            <span className="cvd-kpi-title">Needs your action</span>
            <span className="cvd-stat-badge-purple">
              {kpis?.upcomingTrips || 0} upcoming trips
            </span>
          </div>
          <div className="cvd-kpi-main">
            <strong style={{ color: "#8b5cf6" }}>
              {kpis?.pendingBookings || 0} pending
            </strong>
          </div>
          <div className="cvd-kpi-footer">
            <span>Bookings waiting for your confirmation</span>
            <span className="cvd-kpi-link">Review ➔</span>
          </div>
          <div className="cvd-kpi-glow glow-purple" />
        </div>
      </section>

      {/* ROW 2: FLEET STATUS + FARE CALCULATOR */}
      <section className="cvd-grid-2col">
        <div className="cvd-panel">
          <div className="cvd-panel-header">
            <div>
              <h3>
                <MdSpeed style={{ color: "#38bdf8" }} /> Fleet by cab type
              </h3>
              <p>Live count of your cabs and how many are free right now.</p>
            </div>
            <button
              type="button"
              className="cvd-btn-link-action"
              onClick={() => onNavigate("cabs_manage")}
            >
              Manage fleet ➔
            </button>
          </div>

          <div className="cvd-calc-body">
            {byType.length === 0 ? (
              <div className="cvd-empty-box">
                <p>No cabs added yet. Add your first cab to see fleet details here.</p>
                <button
                  type="button"
                  className="cvd-btn-secondary"
                  onClick={() => onNavigate("cabs_manage")}
                >
                  Add a cab
                </button>
              </div>
            ) : (
              <div className="cvd-calc-receipt">
                {byType.map((t) => (
                  <div className="cvd-receipt-row" key={t.cabType}>
                    <span>{t.cabType}</span>
                    <strong>
                      {t.available} free / {t.count} total
                    </strong>
                  </div>
                ))}
                <div className="cvd-receipt-divider" />
                <div className="cvd-receipt-total">
                  <span>All cabs</span>
                  <strong className="cvd-total-fare">
                    {fleet?.available || 0} / {fleet?.total || 0}
                  </strong>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="cvd-panel cvd-calc-panel">
          <div className="cvd-panel-header">
            <div>
              <h3>
                <MdCalculate style={{ color: "#2563eb" }} /> Fare estimator
              </h3>
              <p>Uses the base fare and per-km rate saved on your cabs.</p>
            </div>
          </div>

          <div className="cvd-calc-body">
            {byType.length === 0 ? (
              <div className="cvd-empty-box">
                <p>Add cabs with a base fare and per-km rate to estimate trip fares.</p>
              </div>
            ) : (
              <>
                <div className="cvd-calc-field">
                  <div className="cvd-calc-field-row">
                    <label>Trip distance:</label>
                    <span className="cvd-calc-number">{calcDistance} KM</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="200"
                    step="1"
                    value={calcDistance}
                    onChange={(e) => setCalcDistance(Number(e.target.value))}
                    className="cvd-range-slider-blue"
                  />
                </div>

                <div className="cvd-calc-field">
                  <label style={{ display: "block", marginBottom: 8, fontSize: 12, fontWeight: 700 }}>
                    Cab type:
                  </label>
                  <div className="cvd-calc-chips">
                    {byType.map((t) => (
                      <button
                        key={t.cabType}
                        type="button"
                        className={`cvd-chip-btn ${calcType === t.cabType ? "active" : ""}`}
                        onClick={() => setCalcType(t.cabType)}
                      >
                        <span>{t.cabType}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="cvd-calc-receipt">
                  <div className="cvd-receipt-row">
                    <span>Base fare (avg):</span>
                    <strong>{money(baseFare)}</strong>
                  </div>
                  <div className="cvd-receipt-row">
                    <span>
                      Distance ({calcDistance} km × {money(perKm)}/km):
                    </span>
                    <strong>{money(kmCharge)}</strong>
                  </div>
                  <div className="cvd-receipt-divider" />
                  <div className="cvd-receipt-total">
                    <span>Estimated fare:</span>
                    <strong className="cvd-total-fare">{money(baseFare + kmCharge)}</strong>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ROW 3: DRIVERS + RECENT BOOKINGS */}
      <section className="cvd-grid-2col" style={{ marginTop: "24px" }}>
        <div className="cvd-panel">
          <div className="cvd-panel-header">
            <div>
              <h3>
                <MdPerson style={{ color: "#8b5cf6" }} /> Drivers
              </h3>
              <p>Drivers assigned to your cabs.</p>
            </div>
            <button
              type="button"
              className="cvd-btn-link-action"
              onClick={() => onNavigate("cabs_drivers")}
            >
              View all ({drivers.length}) ➔
            </button>
          </div>

          <div className="cvd-drivers-list">
            {drivers.length === 0 ? (
              <div className="cvd-empty-box">
                <p>No drivers linked to your cabs yet.</p>
                <button
                  type="button"
                  className="cvd-btn-secondary"
                  onClick={() => onNavigate("cabs_drivers")}
                >
                  Go to driver directory
                </button>
              </div>
            ) : (
              drivers.slice(0, 4).map((d) => (
                <div className="cvd-driver-item" key={d.cabId}>
                  <div className="cvd-driver-meta">
                    <h4>{d.driverName}</h4>
                    <p>
                      Cab: <strong>{d.vehicleNumber}</strong> ({d.cabType})
                      {d.onTrip ? " · on trip" : ""}
                    </p>
                    {d.driverLicenseNumber && <small>DL: {d.driverLicenseNumber}</small>}
                  </div>
                  <div className="cvd-driver-actions">
                    {d.driverPhone && (
                      <a href={`tel:${d.driverPhone}`} className="cvd-btn-action-call" title="Call driver">
                        <MdPhone /> Call
                      </a>
                    )}
                    {digitsOnly(d.driverPhone) && (
                      <a
                        href={`https://wa.me/${digitsOnly(d.driverPhone)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="cvd-btn-action-wa"
                        title="WhatsApp"
                      >
                        <MdSend />
                      </a>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="cvd-panel">
          <div className="cvd-panel-header">
            <div>
              <h3>
                <MdDirectionsCar style={{ color: "#2563eb" }} /> Recent bookings
              </h3>
              <p>Your latest 8 bookings.</p>
            </div>
            <div className="cvd-pill-filter">
              {RIDE_FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`cvd-pill-btn ${rideFilter === f.id ? "active" : ""}`}
                  onClick={() => setRideFilter(f.id)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <div className="cvd-rides-table-wrap">
            {filteredRides.length === 0 ? (
              <div className="cvd-empty-box">
                <p>{rides.length === 0 ? "No bookings yet." : "No bookings match this filter."}</p>
              </div>
            ) : (
              <table className="cvd-table">
                <thead>
                  <tr>
                    <th>Booking & route</th>
                    <th>Cab & driver</th>
                    <th>Fare</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRides.map((r) => {
                    const st = statusView(r.status);
                    return (
                      <tr key={r._id}>
                        <td>
                          <strong style={{ fontSize: 12, color: "#0f172a", display: "block" }}>
                            {r.fromCity || "Pickup"} ➔ {r.toCity || "Drop"}
                          </strong>
                          <small style={{ color: "#64748b" }}>
                            {r.bookingId} · {fmtDate(r.pickupDate)}
                            {r.pickupTime ? ` ${r.pickupTime}` : ""}
                          </small>
                        </td>
                        <td>
                          <span style={{ fontWeight: 700, color: "#1e293b", fontSize: 12 }}>
                            {r.vehicleNumber || r.cabType || "Cab"}
                          </span>
                          <small style={{ display: "block", color: "#64748b" }}>
                            {r.driverName || "No driver yet"}
                          </small>
                        </td>
                        <td>
                          <strong style={{ color: "#059669", fontSize: 13 }}>{money(r.fare)}</strong>
                        </td>
                        <td>
                          <span className={`cvd-badge-status ${st.cls}`}>{st.text}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}